import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { UserProvider, useUserContext } from './contexts/UserContext';
import { useUser } from '@clerk/clerk-react';
import Layout from './components/layout/Layout';
// Import fonts only - not the full globals.css to avoid conflicts with v1
import './styles/v2-scoped.css';
import { useTheme } from './contexts/ThemeContext';

// Lazy load all page components for better performance
const Home = lazy(() => import('./pages/Home'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Pools = lazy(() => import('./pages/Pools'));
const Leagues = lazy(() => import('./pages/Leagues'));
const NCAABasketballAuction = lazy(() => import('./pages/NCAABasketballAuction'));
const NFLBetting = lazy(() => import('./pages/NFLBetting'));
const LiveAuction = lazy(() => import('./pages/LiveAuction'));
const SignInPage = lazy(() => import('./pages/SignIn'));
const SignUpPage = lazy(() => import('./pages/SignUp'));
const SquaresPoolList = lazy(() => import('./pages/SquaresPoolList'));
const SquaresPoolDetail = lazy(() => import('./pages/SquaresPoolDetail'));
const SquaresJoin = lazy(() => import('./pages/SquaresJoin'));
const CreateSquaresPool = lazy(() => import('./pages/CreateSquaresPool'));
const SquaresAdminDashboard = lazy(() => import('./pages/SquaresAdminDashboard'));
const ManageGames = lazy(() => import('./pages/ManageGames'));
const ManageTeams = lazy(() => import('./pages/ManageTeams'));
const ManageBanners = lazy(() => import('./pages/ManageBanners'));
const AdminSettings = lazy(() => import('./pages/AdminSettings'));
const CreditRequests = lazy(() => import('./pages/CreditRequests'));
const About = lazy(() => import('./pages/About'));
const Legal = lazy(() => import('./pages/Legal'));
const Help = lazy(() => import('./pages/Help'));
const ManageAuction = lazy(() => import('./pages/admin/ManageAuction'));
const AdminBidding = lazy(() => import('./pages/admin/AdminBidding'));



// Loading fallback component for lazy-loaded routes
const LoadingFallback = () => {
  const { colors } = useTheme();
  return (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#FAF6F2'
  }}>
    <div style={{
      position: 'relative',
      width: '120px',
      height: '120px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {/* Rotating Circle */}
      <svg
        style={{
          position: 'absolute',
          width: '120px',
          height: '120px',
          animation: 'spin 1.5s linear infinite'
        }}
        viewBox="0 0 120 120"
      >
        <circle
          cx="60"
          cy="60"
          r="54"
          fill="none"
          stroke="#D47A3E"
          strokeWidth="4"
          strokeDasharray="300 360"
          strokeLinecap="round"
        />
      </svg>

      {/* Logo */}
      <img
        src="/img/v2_logo.png"
        alt="Loading"
        style={{
          width: '64px',
          height: '64px',
          position: 'relative',
          zIndex: 1,
          animation: 'bounce 1s ease-in-out infinite'
        }}
      />
    </div>
    <div style={{
      color: colors.text,
      fontSize: '1.25rem',
      fontWeight: 600,
      fontFamily: '"Hubot Sans", sans-serif',
    }}>
      OKRNG
    </div>
  </div>
  );
};

// Inner component that uses UserContext
const V2AppContent = () => {
  const { isSignedIn } = useUser();
  const { user, loading } = useUserContext();
  const [pusher, setPusher] = useState(null);
  const [channel, setChannel] = useState(null);
  const { colors } = useTheme();

  useEffect(() => {
    // Initialize Pusher for real-time auction updates when user is signed in
    if (isSignedIn && user && !pusher) {
      const initPusher = async () => {
        try {
          const Pusher = (await import('pusher-js')).default;
          const pusherInstance = new Pusher(process.env.REACT_APP_PUSHER_KEY, {
            cluster: process.env.REACT_APP_PUSHER_CLUSTER,
          });

          const biddingChannel = pusherInstance.subscribe('bidding-channel');
          setPusher(pusherInstance);
          setChannel(biddingChannel);
        } catch (error) {
          console.error('Error loading Pusher:', error);
        }
      };
      initPusher();
    }

    // Cleanup Pusher on unmount
    return () => {
      if (pusher) {
        pusher.unsubscribe('bidding-channel');
        pusher.disconnect();
      }
    };
  }, [isSignedIn, user, pusher]);

  const handleSignOut = () => {
    window.location.href = '/sign-in';
  };

  if (loading) {
    return (
      <Layout showHeader={false} showFooter={false}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          minHeight: '100vh',
          backgroundColor: '#FAF6F2'
        }}>
          <div style={{
            position: 'relative',
            width: '120px',
            height: '120px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Rotating Circle */}
            <svg
              style={{
                position: 'absolute',
                width: '120px',
                height: '120px',
                animation: 'spin 1.5s linear infinite'
              }}
              viewBox="0 0 120 120"
            >
              <circle
                cx="60"
                cy="60"
                r="54"
                fill="none"
                stroke="#D47A3E"
                strokeWidth="4"
                strokeDasharray="300 360"
                strokeLinecap="round"
              />
            </svg>

            {/* Logo */}
            <img
              src="/img/v2_logo.png"
              alt="Loading"
              style={{
                width: '64px',
                height: '64px',
                position: 'relative',
                zIndex: 1,
                animation: 'bounce 1s ease-in-out infinite'
              }}
            />
          </div>
          <div style={{
            color: colors.text,
            fontSize: '1.25rem',
            fontWeight: 600,
            fontFamily: '"Hubot Sans", sans-serif',
          }}>
            OKRNG
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
          {/* Auth Routes - Without Layout */}
          <Route
            path="/sign-in/*"
            element={<SignInPage />}
          />
          <Route
            path="/sign-up/*"
            element={<SignUpPage />}
          />

          {/* All other routes - With Layout */}
          <Route
            path="/*"
            element={
              <Layout user={user} onSignOut={handleSignOut}>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/legal" element={<Legal />} />
                  <Route path="/help" element={<Help />} />

                  {/* Protected Routes */}
                  <Route
                    path="/dashboard"
                    element={
                      isSignedIn ? (
                        <Dashboard user={user} />
                      ) : (
                        <Navigate to="/sign-in" replace />
                      )
                    }
                  />

                  {/* Pools Routes */}
                  <Route path="/pools" element={<Pools />} />
                  <Route path="/pools/ncaa-basketball-auction" element={<NCAABasketballAuction />} />
                  <Route
                    path="/pools/live-auction"
                    element={
                      isSignedIn ? (
                        <LiveAuction channel={channel} />
                      ) : (
                        <Navigate to="/sign-in" replace />
                      )
                    }
                  />
                  <Route
                    path="/pools/nfl"
                    element={
                      isSignedIn ? (
                        <NFLBetting />
                      ) : (
                        <Navigate to="/sign-in" replace />
                      )
                    }
                  />

                  {/* Leagues Route */}
                  <Route path="/leagues" element={<Leagues />} />

                  {/* Squares Routes */}
                  <Route path="/squares" element={<SquaresPoolList />} />
                  <Route path="/squares/join" element={<SquaresJoin />} />
                  <Route path="/squares/pool/:poolNumber" element={<SquaresPoolDetail />} />
                  <Route path="/credit-requests" element={<CreditRequests />} />
                  <Route
                    path="/squares/create"
                    element={
                      isSignedIn ? (
                        <CreateSquaresPool />
                      ) : (
                        <Navigate to="/sign-in" replace />
                      )
                    }
                  />
                  <Route
                    path="/squares/admin"
                    element={
                      isSignedIn ? (
                        <SquaresAdminDashboard />
                      ) : (
                        <Navigate to="/sign-in" replace />
                      )
                    }
                  />
                  <Route
                    path="/games/manage"
                    element={
                      isSignedIn ? (
                        <ManageGames />
                      ) : (
                        <Navigate to="/sign-in" replace />
                      )
                    }
                  />

                  {/* Admin Routes - Superadmin Only */}
                  <Route
                    path="/admin/settings"
                    element={
                      isSignedIn && user?.role_id === 1 ? (
                        <AdminSettings />
                      ) : (
                        <Navigate to="/" replace />
                      )
                    }
                  />
                  <Route
                    path="/admin/auction"
                    element={
                      isSignedIn && user?.role_id === 1 ? (
                        <ManageAuction />
                      ) : (
                        <Navigate to="/" replace />
                      )
                    }
                  />
                  <Route
                    path="/admin/auction/live"
                    element={
                      isSignedIn && user?.role_id === 1 ? (
                        <AdminBidding channel={channel} />
                      ) : (
                        <Navigate to="/" replace />
                      )
                    }
                  />
                  <Route
                    path="/admin/teams"
                    element={
                      isSignedIn && user?.role_id <= 2 ? (
                        <ManageTeams />
                      ) : (
                        <Navigate to="/" replace />
                      )
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      isSignedIn && user?.role_id === 1 ? (
                        <ComingSoon title="User Management" />
                      ) : (
                        <Navigate to="/" replace />
                      )
                    }
                  />
                  <Route
                    path="/admin/banners"
                    element={
                      isSignedIn && user?.role_id === 1 ? (
                        <ManageBanners />
                      ) : (
                        <Navigate to="/" replace />
                      )
                    }
                  />

                  {/* Placeholder routes for future pages */}
                  <Route path="/betting" element={<NCAABasketballAuction />} />
                  <Route path="/settings" element={<ComingSoon title="Settings" />} />
                  <Route path="/activity" element={<ComingSoon title="Activity" />} />

                  {/* Catch all - redirect to home */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Layout>
            }
          />
        </Routes>
      </Suspense>
  );
};

// Main V2App wrapper with providers
const V2App = () => {
  return (
    <ThemeProvider>
      <UserProvider>
        <V2AppContent />
      </UserProvider>
    </ThemeProvider>
  );
};

// Coming Soon Component for placeholder pages
const ComingSoon = ({ title }) => {
  const containerStyles = {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '5rem 2rem',
    textAlign: 'center',
  };

  const titleStyles = {
    fontSize: '3rem',
    fontWeight: 800,
    fontFamily: '"Hubot Sans", sans-serif',
    marginBottom: '1rem',
  };

  const descStyles = {
    fontSize: '1.25rem',
    opacity: 0.7,
  };

  return (
    <div style={containerStyles} className="v2-fade-in">
      <h1 style={titleStyles}>{title}</h1>
      <p style={descStyles}>This page is coming soon. Stay tuned!</p>
    </div>
  );
};

export default V2App;
